import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { PageLayout } from './components';
import MsalWrapper from './msal-wrapper';
import { BenchmarkRoute, InternalUser, NotFoundRoute, PortStateControlRoute } from './routes';
import ConfigService from './services/config.service';

const AppRoutes = () => {
    const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);

    const azureB2CInstance = new PublicClientApplication({ auth: ConfigService.azureB2CConfig.auth, cache: ConfigService.azureB2CConfig.cache });
    const azureAdInstance = new PublicClientApplication({ auth: ConfigService.azureADConfig.auth, cache: ConfigService.azureADConfig.cache });

    // Intercept http request and append the Authorization header
    axios.interceptors.request.use(
        async (request) => {
            if (!msalInstance) return request;

            await msalInstance.initialize();
            const account = msalInstance.getAllAccounts();

            let req: any | undefined = undefined;
            if (sessionStorage.getItem('isInternal') === 'true') {
                // Azure AD login
                req = { scopes: ConfigService.azureADScope.scopes };
            } else {
                // Azure B2C login
                req = { scopes: ConfigService.azureB2CScope.scopes };
            }

            const res = await msalInstance.acquireTokenSilent({ scopes: req.scopes, account: account[0] });
            request.headers.Authorization = `Bearer ${res.idToken}`;

            const impersonatedUser = sessionStorage.getItem('impersonatedUser');
            if (impersonatedUser) {
                request.headers.ImpersonateUser = impersonatedUser;
            }

            return request;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    useEffect(() => {
        if (!msalInstance) {
            const params = new URLSearchParams(window.location.search);
            const impersonatedUser = params.get('impersonatedUser');

            if (window.location.href.includes('internal') || sessionStorage.getItem('isInternal') === 'true') {
                sessionStorage.setItem('isInternal', 'true');

                if (impersonatedUser && impersonatedUser !== '') {
                    sessionStorage.setItem('impersonatedUser', impersonatedUser);
                }

                setMsalInstance(azureAdInstance);
            } else {
                sessionStorage.setItem('isInternal', 'false');
                setMsalInstance(azureB2CInstance);
            }
        }
    }, [msalInstance, azureAdInstance, azureB2CInstance]);

    const getRoutes = (): JSX.Element => {
        // Default Routes
        const routes = [<Route key="index" index element={<PortStateControlRoute />} />, <Route key="internal" path="internal" element={<InternalUser />} />];

        // Benchmark Route
        if (ConfigService.appConfig.enableBenchmarking) {
            routes.push(<Route key="benchmarking" path="benchmarking" element={<BenchmarkRoute />} />);
        }

        return <>{routes}</>;
    };

    return (
        <>
            {msalInstance && (
                <MsalProvider instance={msalInstance}>
                    <MsalWrapper>
                        <BrowserRouter>
                            <Routes>
                                <Route path="/" element={<PageLayout />}>
                                    {getRoutes()}
                                </Route>
                                <Route path="*" element={<NotFoundRoute />} />
                            </Routes>
                        </BrowserRouter>
                    </MsalWrapper>
                </MsalProvider>
            )}
        </>
    );
};

export default AppRoutes;
